import {
  formatUrlForDisplay,
  isLikelyUrl
} from '../../utils/threadHistoryDisplay';

export type SkillFlowStepOption = {
  id: string;
  label: string;
  description?: string;
};

export type SkillFlowStep = {
  id: string;
  title: string;
  description?: string;
  /** group = several fields on one step (see `fields`); answers stay per field. */
  inputType: 'select' | 'url' | 'textarea' | 'text' | 'domain' | 'group';
  fields?: SkillFlowStep[];
  selectionMode?: 'single' | 'multi';
  /** buttons = pill/list (default); dropdown = searchable select for long catalogs. */
  selectionUi?: 'buttons' | 'dropdown';
  options?: SkillFlowStepOption[];
  optional?: boolean;
  placeholder?: string;
  /** Pre-selected option ids when the configure wizard opens. */
  defaultSelectedIds?: string[];
  dependsOn?: Record<string, string[]>;
  /** Textarea: min non-empty lines (one phrase per line). Omit = no min check. */
  minLines?: number | null;
  /** Textarea: max non-empty lines. Omit = no max check. */
  maxLines?: number | null;
};

export type ConfigureSkillToolArgs = {
  id: string;
  skillCommand: string;
  skillExecutionId: string;
  title: string;
  description?: string;
  steps: SkillFlowStep[];
};

export type ConfigurePromptTemplateToolArgs = {
  id: string;
  promptTemplateId: string;
  title: string;
  description?: string;
  steps: SkillFlowStep[];
  configureMessageId?: string;
};

export type SkillConfigureAnswersPayload = {
  phase: 'answers';
  skillCommand: string;
  skillExecutionId?: string;
  answers: Record<string, string[]>;
};

export type PromptTemplateConfigureAnswersPayload = {
  phase: 'answers';
  promptTemplateId: string;
  answers: Record<string, string[]>;
};

export type SkillFlowChoiceSummary = {
  label: string;
  value: string;
};

function stepVisible(
  step: SkillFlowStep,
  answers: Record<string, string[]>
): boolean {
  if (!step.dependsOn) return true;
  return Object.entries(step.dependsOn).every(([key, expected]) => {
    const selected = answers[key] ?? [];
    return expected.some((value) => selected.includes(value));
  });
}

/** Leaf input steps (group steps replaced by their fields). */
export function flattenSkillFlowSteps(steps: SkillFlowStep[]): SkillFlowStep[] {
  return steps.flatMap((step) => step.fields ?? [step]);
}

export function visibleSkillFlowSteps(
  steps: SkillFlowStep[],
  answers: Record<string, string[]>
): SkillFlowStep[] {
  return steps.filter((step) => stepVisible(step, answers));
}

/** Prepend same/new URL step when a prior skill URL exists in this chat. */
export function withUrlReuseStep(
  steps: SkillFlowStep[],
  priorUrl: string
): SkillFlowStep[] {
  const hasUrlStep = steps.some((step) => step.id === 'url');
  if (!hasUrlStep || !priorUrl.trim()) {
    return steps;
  }

  const reuseStep: SkillFlowStep = {
    id: '_url_reuse',
    title: 'آدرس صفحه',
    description: 'می‌خواهید روی همان صفحه قبلی کار کنیم یا آدرس جدید بدهید؟',
    inputType: 'select',
    selectionMode: 'single',
    options: [
      {
        id: 'same',
        label: 'همین آدرس',
        description: priorUrl
      },
      {
        id: 'new',
        label: 'آدرس جدید',
        description: 'آدرس یا HTML صفحه دیگری را وارد کنید'
      }
    ]
  };

  const mapped = steps.map((step) => {
    if (
      step.id !== '_source_mode' &&
      step.id !== 'url' &&
      step.id !== 'html'
    ) {
      return step;
    }
    return {
      ...step,
      dependsOn: {
        ...(step.dependsOn ?? {}),
        _url_reuse: ['new']
      }
    };
  });

  return [reuseStep, ...mapped];
}

export function formatDomainAnswerLabel(
  slug: string,
  domains: Array<{ slug: string; url?: string; show_name?: string | null }>
): string {
  const matched = domains.find((domain) => domain.slug === slug);
  if (!matched) return slug;
  const address = (matched.url || slug).trim();
  const displayName = matched.show_name?.trim();
  return displayName ? `${address} (${displayName})` : address;
}

export function summarizeSkillAnswers(
  steps: SkillFlowStep[],
  answers: Record<string, string[]>,
  options?: {
    domains?: Array<{ slug: string; url?: string; show_name?: string | null }>;
  }
): SkillFlowChoiceSummary[] {
  const domains = options?.domains ?? [];
  // Receipt should show submitted values even if wizard dependsOn
  // (e.g. `_source_mode`) is missing after refresh/hydration.
  return flattenSkillFlowSteps(steps)
    .filter((step) => !step.id.startsWith('_'))
    .map((step) => {
      const values = answers[step.id] ?? [];
      const isDomain =
        step.inputType === 'domain' || step.id === 'domain_slug';
      const labels =
        step.options && step.options.length > 0
          ? values
              .map(
                (value) =>
                  step.options?.find((option) => option.id === value)?.label ??
                  value
              )
              .join('، ')
          : isDomain
            ? values
                .map((value) => formatDomainAnswerLabel(value, domains))
                .join('، ')
            : values.join('، ');
      const raw = labels || '—';
      const value =
        raw !== '—' &&
        (step.inputType === 'url' ||
          step.id === 'url' ||
          isLikelyUrl(raw))
          ? formatUrlForDisplay(raw)
          : raw;
      return {
        label: step.title,
        value
      };
    })
    .filter((row) => row.value !== '—');
}

export function isConfigureSkillToolArgs(
  value: unknown
): value is ConfigureSkillToolArgs {
  if (!value || typeof value !== 'object') return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === 'string' &&
    typeof row.skillCommand === 'string' &&
    typeof row.skillExecutionId === 'string' &&
    Array.isArray(row.steps)
  );
}

export function isConfigurePromptTemplateToolArgs(
  value: unknown
): value is ConfigurePromptTemplateToolArgs {
  if (!value || typeof value !== 'object') return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === 'string' &&
    typeof row.promptTemplateId === 'string' &&
    Array.isArray(row.steps)
  );
}

export function isSkillConfigureAnswersPayload(
  value: unknown
): value is SkillConfigureAnswersPayload {
  if (!value || typeof value !== 'object') return false;
  const row = value as Record<string, unknown>;
  return (
    row.phase === 'answers' &&
    typeof row.skillCommand === 'string' &&
    !!row.answers &&
    typeof row.answers === 'object'
  );
}

export function isPromptTemplateConfigureAnswersPayload(
  value: unknown
): value is PromptTemplateConfigureAnswersPayload {
  if (!value || typeof value !== 'object') return false;
  const row = value as Record<string, unknown>;
  return (
    row.phase === 'answers' &&
    typeof row.promptTemplateId === 'string' &&
    !!row.answers &&
    typeof row.answers === 'object'
  );
}
